import { useState, useCallback, useRef, useEffect } from 'react';

interface UseInfiniteScrollOptions<T> {
  fetchData: (page: number, pageSize: number) => Promise<{
    list: T[];
    total: number;
  } | null>;
  pageSize?: number;
  enabled?: boolean;
  initialData?: T[];
}

interface UseInfiniteScrollReturn<T> {
  data: T[];
  loading: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => Promise<void>;
  reset: () => void;
  resetAndFetch: () => Promise<void>;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}

export function useInfiniteScroll<T>({
  fetchData,
  pageSize = 20,
  enabled = true,
  initialData = []
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn<T> {
  const [data, setData] = useState<T[]>(initialData);
  const [loading, setLoading] = useState(enabled); // 如果 enabled 为 true，初始状态为 loading
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const scrollRef = useRef<HTMLDivElement>(null);

  // 加载更多数据
  const loadMore = useCallback(async () => {
    if (loading || !hasMore || !enabled) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchData(currentPage, pageSize);

      if (result?.list && Array.isArray(result.list)) {
        setData(prev => currentPage === 1 ? result.list : [...prev, ...result.list]);
        setHasMore(result.total > currentPage * pageSize);
        setCurrentPage(prev => prev + 1);
      }
    } catch (err) {
      setError(err as Error);
      console.error('load data failed:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, loading, hasMore, enabled, pageSize]);

  // 重置数据
  const reset = useCallback(() => {
    setData(initialData);
    setCurrentPage(1);
    setHasMore(true);
    setError(null);
  }, [initialData]);

  // 重置数据并立即加载第一页
  const resetAndFetch = useCallback(async () => {
    if (!enabled) return;

    // 重置状态
    reset();
    setLoading(true);

    try {
      const result = await fetchData(1, pageSize);

      if (result?.list && Array.isArray(result.list)) {
        setData(result.list);
        setHasMore(result.total > pageSize);
        setCurrentPage(2); // 下次加载从第2页开始
      }
    } catch (err) {
      setError(err as Error);
      console.error('reset and load data failed:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reset, pageSize]);

  // 滚动事件处理
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || loading || !hasMore || !enabled) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 距离底部100px时触发

    if (isNearBottom) {
      loadMore();
    }
  }, [loadMore, loading, hasMore, enabled]);

  // 绑定滚动事件
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !enabled) return;

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [handleScroll, enabled]);

  return {
    data,
    loading,
    hasMore,
    error,
    loadMore,
    reset,
    resetAndFetch,
    scrollRef
  };
}
